import { Autocomplete, Grid, Skeleton, TextField, FormHelperText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useStatisticsContext } from 'src/shared/contexts/statisticsContext';
import { useEffect, useState } from 'react';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import dayjs from 'dayjs';
import { SnapshotTimeResponse } from '../models/snapshotTimeResponse';
import { useFetchSnapshots } from '../hooks/useFetchSnapshots';

export const StatisticsFilterAutocompleteDatePicker = () => {
  const { t } = useTranslation();

  const { spans, isSnapshotLoading } = useFetchSnapshots();
  const { memberGrowthSearchFilter, setMemberGrowthSearchFilter } = useStatisticsContext();

  const [toDateError, setToDateError] = useState('');

  useEffect(() => {
    if (spans.length > 0 && !memberGrowthSearchFilter.fromSnapshotTime) {
      setMemberGrowthSearchFilter((prevState) => ({
        ...prevState,
        fromSnapshotTime: spans[1],
        toSnapshotTime: spans[0],
      }));
    }
  }, [spans, setMemberGrowthSearchFilter, memberGrowthSearchFilter.fromSnapshotTime]);

  const validateDates = (fromDate, toDate) => {
    if (toDate && fromDate) {
      const from = dayjs(fromDate.snapshotTime);
      const to = dayjs(toDate.snapshotTime);
      if (dayjs(to).isBefore(from)) {
        setToDateError(t('statistics:memberProgress.errors.toDateBeforeFromDate'));
        return false;
      }
      if (dayjs(to).isSame(from)) {
        setToDateError(t('statistics:memberProgress.errors.toDateEqualFromDate'));
        return false;
      }
    }
    setToDateError('');
    return true;
  };

  const handleFromDateChange = (_e, newValue) => {
    setMemberGrowthSearchFilter((prev) => {
      const updatedFilter = { ...prev, fromSnapshotTime: newValue };
      validateDates(newValue, prev.toSnapshotTime);
      return updatedFilter;
    });
  };

  const handleToDateChange = (_e, newValue) => {
    setMemberGrowthSearchFilter((prev) => {
      const updatedFilter = { ...prev, toSnapshotTime: newValue };
      if (validateDates(prev.fromSnapshotTime, newValue)) {
        return updatedFilter;
      }
      return prev;
    });
  };

  const isOptionEqualToValue = (option: SnapshotTimeResponse, value: SnapshotTimeResponse) => option.snapshotTime_id === value.snapshotTime_id;

  if (!spans) {
    return null;
  }

  return (
    <>
      <Grid item xs={12} lg={3}>
        {isSnapshotLoading || !memberGrowthSearchFilter.fromSnapshotTime ? (
          <Skeleton variant='rectangular' animation='wave' sx={{ height: '40px', width: '100%', borderRadius: '4px' }} />
        ) : (
          <>
            <Autocomplete<SnapshotTimeResponse>
              value={memberGrowthSearchFilter.fromSnapshotTime}
              isOptionEqualToValue={isOptionEqualToValue}
              getOptionLabel={(option) => DateHelper.getDate(option.snapshotTime) || ''}
              options={spans}
              onChange={handleFromDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={t('statistics:memberProgress.labels.timeFrom')}
                  size='small'
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </>
        )}
      </Grid>

      <Grid item xs={12} lg={3}>
        {isSnapshotLoading || !memberGrowthSearchFilter.toSnapshotTime ? (
          <Skeleton variant='rectangular' animation='wave' sx={{ height: '40px', width: '100%', borderRadius: '4px' }} />
        ) : (
          <>
            <Autocomplete<SnapshotTimeResponse>
              value={memberGrowthSearchFilter.toSnapshotTime}
              isOptionEqualToValue={isOptionEqualToValue}
              getOptionLabel={(option) => DateHelper.getDate(option.snapshotTime) || ''}
              options={spans}
              onChange={handleToDateChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('statistics:memberProgress.labels.timeTo')}
                  size='small'
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(toDateError)}
                />
              )}
            />
          </>
        )}
        {toDateError && <FormHelperText error>{toDateError}</FormHelperText>}
      </Grid>
    </>
  );
};
